import { useState, useEffect } from 'react'
import { chakra } from '@chakra-ui/react'
import { Pressable } from '@ayble-health/dashboard-components'
import './careos-prototype.css'
import LabsTab from './LabsTab'
import PharmacyTab from './PharmacyTab'
import type { JourneyStage, ActiveLabOrder, AppStatus, ClaimsInsightsState } from '../../models/careOSPrototype'
import { ChevronLeft, ChevronDown, ChevronRight, FileIcon, UserIcon, PlusIcon } from './icons'
import { FollowUpsCard } from './FollowUpsCard'
import { StatCard, StatCardDouble } from './StatCard'
import { GutCheckAppStatus } from './GutCheckAppStatus'
import { PatientSidebar } from './PatientSidebar'
import { LaneBlockCard, type LaneBlockCardProps } from './LaneBlockCard'
import { PreVisitClaimsInsights } from './PreVisitClaimsInsights'
import { Chip } from 'components/ui/chip'
import { CareContinuityPanel } from './CareContinuityPanel'
import { CareBlockDrawer } from './CareBlockDrawer'
import { PatientSummaryDrawer } from './PatientSummaryDrawer'
import { useCareOsPatient } from '../../api/careOSPrototype/useCareOsPatient'
import { useCareBlockDetail } from '../../api/careOSPrototype/useCareBlockDetail'
import type { CareBlockDetail, SymptomsFeedback } from '../../models/careBlockDetail'

interface Props {
  patientId: string
  onBack: () => void
  initialTab?: string
  /** Fired whenever the profile's top-level tab changes. The prototype bar uses
   *  it (wired up in `PatientProfileRoute`) to show only the controls relevant
   *  to the visible tab. */
  onTabChange?: (tab: string) => void
  journeyStage?: JourneyStage
  setJourneyStage?: (s: JourneyStage) => void
  activeLabOrder?: ActiveLabOrder | null
  setActiveLabOrder?: (order: ActiveLabOrder | null) => void
  onLabOrderComplete?: () => void
  /** Prototype override for the Gut Checks app-status pill (from the control
   *  bar). Falls back to the patient fixture's own status when omitted. */
  appStatus?: AppStatus
  /** Prototype demo scenario (from the control bar) driving what the Patient
   *  Summary tab shows: before a first visit, the claims insights alone, or
   *  the onboarding chief complaint with the claims insights beneath it;
   *  after the first visit, the transcript chief complaint alone. Defaults to
   *  'before-first-visit'. */
  claimsState?: ClaimsInsightsState
}

// Patient Overview panel tabs. Always the same two — claims insights are not
// a tab of their own, they're the Patient Summary tab's pre-first-visit state.
type PatientOverviewTab = 'patient-summary' | 'care-continuity'

const PATIENT_TAB_META: Record<PatientOverviewTab, { label: string; Icon: () => JSX.Element }> = {
  'patient-summary': { label: 'Patient Summary', Icon: FileIcon },
  'care-continuity': { label: 'Uncovered Goals', Icon: UserIcon },
}

// Kanban data — shape matches `<LaneBlockCard>` props. Card color is driven by
// the card's status header, NOT the column. Lane only carries the dot color.
//
// All six card states are exercised here, so the board is a true sample of what
// LaneBlockCard can render rather than the four states that happened to be
// needed first. `error` and `archived` were implemented in the recipe from the
// start and never used — see the Patterns tab, which documents the set.
//
// Placement of `archived` is a product question, not a styling one: it sits in
// its own lane rather than a separate column, on the reading that a coach
// scanning Nutrition benefits from seeing what was already tried. The Figma
// state chart shows Archived as its own column, which is a component-state
// document, not a Care Guide layout.
//
// `id` is a stable slug, not a positional key: it's the lookup into the Care
// Block Detail fixtures (`mocks/fixtures/careBlockDetail.ts`) that back the
// drawer, so renaming one here without renaming it there orphans the block.
type LaneBlock = LaneBlockCardProps & { id: string }

// The "today" relative note dates are measured against. Pinned, never derived.
const CARE_PLAN_AS_OF = '2026-10-26'

// The newest symptoms answer in a block's feed. Activity runs oldest → newest,
// so walk it backwards.
function latestSymptoms(block: CareBlockDetail | undefined): SymptomsFeedback | undefined {
  const activity = block?.activity ?? []
  for (let i = activity.length - 1; i >= 0; i--) {
    const entry = activity[i]
    if (entry.kind === 'care-team-feedback' && entry.symptoms) return entry.symptoms
  }
  return undefined
}

// A trouble-implementing card's pill is the block's latest symptoms feedback,
// so it reads the same detail query the drawer does — submitting feedback
// invalidates it, and the pill updates with the feed. Other cards skip the fetch.
function KanbanCard({ block, onOpen }: { block: LaneBlock; onOpen: () => void }) {
  const { id, ...cardProps } = block
  const wantsSymptoms = cardProps.notes?.variant === 'trouble-implementing'
  const { block: detail } = useCareBlockDetail(wantsSymptoms ? id : null)
  const notes = cardProps.notes && wantsSymptoms
    ? { ...cardProps.notes, symptoms: latestSymptoms(detail) }
    : cardProps.notes
  return <LaneBlockCard {...cardProps} notes={notes} onOpen={onOpen} />
}

const CARE_PLAN_LANES: { id: string; label: string; color: string; blocks: LaneBlock[] }[] = [
  {
    id: 'clinical', label: 'Clinical', color: 'var(--seafoam)',
    blocks: [
      { id: 'lab-work-new', variant: 'completed',   title: 'Complete New Lab Work',   assignedBy: 'T. Haywood, MD', headerLabel: 'Completed', showViewResults: true, notes: { variant: 'out-of-range', count: 2 } },
      { id: 'lab-work-previous', variant: 'overdue',     title: 'Share Previous Lab Work', assignedBy: 'T. Haywood, MD', headerLabel: '14 days ago', progress: { dueLabel: 'Jan 7' } },
      { id: 'modify-medication', variant: 'in-progress', title: 'Modify Medication',       assignedBy: 'T. Haywood, MD', progress: { dueLabel: 'Jan 7' } },
      { id: 'lab-order-transmit', variant: 'error',       title: 'Send Lab Order to Labcorp', assignedBy: 'T. Haywood, MD', headerLabel: 'Error' },
    ],
  },
  {
    id: 'nutrition', label: 'Nutrition', color: 'var(--aqua)',
    blocks: [
      { id: 'elimination-challenge', variant: 'in-progress', title: 'Elimination Challenge', assignedBy: 'J. Brown, RD',
        engagement: { lastEngaged: 'Today, 9:12 AM ET', count: 14, total: 21, countNoun: 'Gut Checks' } },
      // Notes count reviewed entries only — Coach Carol's two and the two
      // accepted Ayble AI entries. The Oct 27 draft isn't on the record until
      // someone accepts it, so the last update is Aug 27.
      { id: 'focus-on-fiber', variant: 'in-progress', title: 'Focus on fiber', assignedBy: 'Coach Carol',
        notes: { variant: 'trouble-implementing', count: 4, extraCount: 3, lastAddedOn: '2026-08-27', asOf: CARE_PLAN_AS_OF } },
      { id: 'trigger-foods', variant: 'completed',   title: 'Identify Suspected Trigger Foods', assignedBy: 'J. Brown, RD', headerLabel: '4 Triggers Found', showViewResults: true },
      { id: 'food-challenge', variant: 'suggested',   title: 'Food Challenge', assignedBy: 'Suggested by Ayble',
        statCallout: <><strong>Why?</strong> This interventions helps 78% of people like Sandy with bloating.</> },
      { id: 'low-fodmap-reintro', variant: 'archived',  title: 'Low-FODMAP Reintroduction', assignedBy: 'J. Brown, RD' },
    ],
  },
  {
    id: 'mind-gut', label: 'Mind-Gut', color: 'var(--scrubs)',
    blocks: [
      { id: 'mind-gut-masterclass', variant: 'in-progress', title: 'Mind-Gut Masterclass',  assignedBy: 'GDH',
        engagement: { lastEngaged: 'Yesterday, 6:55 PM ET', count: 12 } },
      { id: 'deep-breathing', variant: 'in-progress', title: 'Exercise: Deep Breathing', assignedBy: 'E. Rayas, Coach' },
    ],
  },
  {
    id: 'behavior-change', label: 'Behavior Change', color: 'var(--blue-grey)',
    blocks: [
      { id: 'stay-hydrated', variant: 'overdue', title: 'Stay Hydrated', assignedBy: 'E. Rayas, Coach', headerLabel: '14 days ago',
        engagement: { lastEngaged: '14 days ago', count: 0, isRecent: false, streakEndedDays: 8 },
        notes: { variant: 'trouble-implementing', count: 3, extraCount: 2, lastAddedOn: '2026-02-06', asOf: CARE_PLAN_AS_OF } },
    ],
  },
]

const TOP_NAV_ITEMS = ['Overview', 'Progress', 'Appointment', 'Labs & Documents', 'Pharmacy']

export function PatientProfile({ patientId, onBack, initialTab, onTabChange, journeyStage, setJourneyStage, activeLabOrder, setActiveLabOrder, onLabOrderComplete, appStatus, claimsState }: Props) {
  const effectiveClaimsState = claimsState ?? 'before-first-visit'
  const isSteadyState = effectiveClaimsState === 'steady-state'
  // Still before the first visit, but onboarding gives the member's chief
  // complaint: the tab leads with it and keeps the claims insights beneath.
  const isSummaryReady = effectiveClaimsState === 'summary-ready'

  const [activeNav, setActiveNav] = useState(initialTab ?? 'Overview')

  // Report the active tab out on mount and on every change, so a listener sees
  // the initial tab too and not just subsequent switches.
  useEffect(() => {
    onTabChange?.(activeNav)
  }, [activeNav, onTabChange])
  // Patient Summary is always the default tab — before a first visit its body
  // is the pre-visit claims insights, so the "front and center" behavior the
  // PRD asks for falls out of the content swap, not a tab-set change.
  const [activePatientTab, setActivePatientTab] = useState<PatientOverviewTab>('patient-summary')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  // Care block whose detail drawer is open; null when closed. Held here rather
  // than inside the Care Guide so the drawer can render as a sibling of the
  // whole profile, above the right rail.
  const [openBlockId, setOpenBlockId] = useState<string | null>(null)
  const [summaryOpen, setSummaryOpen] = useState(false)
  // The block and summary drawers share the right edge: opening one closes the other.
  const openBlock = (id: string | null) => { setSummaryOpen(false); setOpenBlockId(id) }
  const openSummary = () => { setOpenBlockId(null); setSummaryOpen(true) }

  const { patient, isLoading } = useCareOsPatient(patientId)
  // The goal's own drawer feed. Read here too so the goal card's tag tracks the
  // latest symptom answer: submitting from the drawer invalidates this query.
  const { block: goalBlock } = useCareBlockDetail(patient?.careGoalBlockId ?? null)
  // Feed runs oldest → newest, so the last symptom answer wins.
  const goalSymptomStatus = goalBlock?.activity.reduce<SymptomsFeedback | undefined>(
    (latest, entry) => (entry.kind === 'care-team-feedback' && entry.symptoms) || latest,
    undefined,
  )

  // careos-root scopes the CSS-var layer (var(--evergreen) etc.) to this subtree.
  if (!patient) {
    return (
      <chakra.div
        className="careos-root"
        css={{ background: '{colors.canvas}' }}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--blue-grey)', fontFamily: 'var(--font-body)', fontSize: 'var(--text-sm-size)' }}
      >
        {isLoading ? 'Loading…' : 'Patient not found'}
      </chakra.div>
    )
  }

  // Control-bar override wins over the patient fixture's own status.
  const resolvedAppStatus = appStatus ?? patient.gutChecks.appStatus

  return (
    <chakra.div className="careos-root" css={{ background: '{colors.canvas}' }} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>

      {/* Page header */}
      <chakra.div
        css={{ background: '{colors.canvas}' }}
        style={{ height: 60, display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0, gap: 16 }}
      >
        <Pressable
          onClick={onBack}
          aria-label="Back to home"
          className="hover-evergreen"
          display="flex"
          alignItems="center"
          color="bluegrey"
          p={0}
          transition="color 0.15s"
        >
          <ChevronLeft />
        </Pressable>
        <h1 style={{
          fontFamily: 'var(--font-heading)', fontSize: 'var(--text-h3-size)', fontWeight: 'var(--text-h3-weight)',
          color: 'var(--evergreen)', margin: 0,
        }}>
          {patient.name}
        </h1>
      </chakra.div>

      {/* Top nav bar */}
      <chakra.div
        css={{ background: '{colors.canvas}', borderBottom: '1px solid', borderBottomColor: '{colors.cloud}' }}
        style={{ height: 42, display: 'flex', alignItems: 'flex-end', padding: '0 20px', flexShrink: 0, gap: 4 }}
      >
        {TOP_NAV_ITEMS.map(item => {
          const isActive = activeNav === item
          return (
            <Pressable
              key={item}
              onClick={() => setActiveNav(item)}
              className={!isActive ? 'hover-evergreen' : ''}
              style={{
                padding: '8px 8px 7px',
                marginBottom: -1,
                fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)',
                color: isActive ? 'var(--evergreen)' : 'var(--blue-grey)',
                borderBottom: `2px solid ${isActive ? 'var(--aqua)' : 'transparent'}`,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'color 0.15s',
                background: 'transparent',
                // Reset only the non-bottom borders so the `border` shorthand
                // can't clobber `borderBottom` — every tab keeps a consistent
                // 2px bottom border (transparent when inactive), so all labels
                // sit at the same height regardless of which tab is active.
                borderTop: 'none', borderRight: 'none', borderLeft: 'none',
              }}
            >
              {item}
            </Pressable>
          )
        })}
      </chakra.div>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* Scrollable main content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {activeNav === 'Overview' ? (
            <div style={{ padding: '16px 20px 40px' }}>

                  {/* Summary row */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>

                    {/* Left — Patient Overview (C_Patient Overview spec) */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <chakra.div
                        css={{ background: '{colors.feather}', border: '1px solid', borderColor: '{colors.cloud}', borderRadius: '{radii.card}' }}
                        style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                      >
                        {/* Tab row + risk strat (pl-8 pr-16, no vertical padding) */}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          paddingLeft: 8, paddingRight: 16, height: 42,
                        }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {(['patient-summary', 'care-continuity'] as const).map(tab => {
                              const selected = activePatientTab === tab
                              const { label, Icon } = PATIENT_TAB_META[tab]
                              return (
                                <Pressable
                                  key={tab}
                                  onClick={() => setActivePatientTab(tab)}
                                  className={!selected ? 'hover-seafoam' : ''}
                                  flexDirection="row"
                                  alignItems="center"
                                  style={{
                                    display: 'flex', gap: 8,
                                    height: 42, padding: '0 16px',
                                    fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', letterSpacing: '0.02em',
                                    fontFamily: 'var(--font-body)',
                                    color: selected ? 'var(--evergreen)' : 'var(--blue-grey)',
                                    background: 'transparent',
                                    borderTop: 'none', borderRight: 'none', borderLeft: 'none',
                                    borderBottom: `2px solid ${selected ? 'var(--aqua)' : 'transparent'}`,
                                    cursor: 'pointer', transition: 'color 0.15s',
                                  }}
                                >
                                  <Icon />
                                  {label}
                                </Pressable>
                              )
                            })}
                          </div>
                          {/* Summary + Claims: no visit-derived risk yet, so the header says
                              where this early summary comes from instead. */}
                          {isSummaryReady && <Chip variant="mint">From onboarding and claims</Chip>}
                        </div>

                        {/* Body region — top divider, 16 padding, 16 inner gap */}
                        <div style={{ borderTop: '1px solid var(--steel)' }} />
                        <div style={{
                          padding: 16, flex: 1,
                          display: 'flex', flexDirection: 'column', gap: 16,
                        }}>
                          {activePatientTab === 'patient-summary' ? (
                            !isSteadyState && !isSummaryReady ? (
                              // Before the first visit there's no prior care to
                              // synthesize — the summary IS the claims-derived
                              // pre-visit insights (handles no-claims-data too).
                              <PreVisitClaimsInsights state={effectiveClaimsState} />
                            ) : (
                            <>
                              {isSteadyState ? (
                                // After the first visit: the transcript chief complaint, in a card
                                // that opens the Patient Summary drawer — the same card and chevron
                                // as the Uncovered Goals goal, which opens its block drawer.
                                <div
                                  className="hover-shadow"
                                  role="button"
                                  tabIndex={0}
                                  aria-haspopup="dialog"
                                  aria-label="Open patient summary details"
                                  onClick={openSummary}
                                  onKeyDown={(e: React.KeyboardEvent) => {
                                    // Space/Enter activate, matching a native button.
                                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSummary() }
                                  }}
                                  style={{
                                    padding: '16px 20px',
                                    background: 'var(--canvas)', borderRadius: 8,
                                    cursor: 'pointer',
                                  }}
                                >
                                  <p style={{
                                    margin: 0,
                                    fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em',
                                    color: 'var(--evergreen)', fontFamily: 'var(--font-body)',
                                  }}>
                                    {patient.chiefComplaint}
                                  </p>
                                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                    {/* Affordance that the card opens. */}
                                    <span
                                      aria-hidden
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: 24, height: 24, color: 'var(--blue-grey)',
                                      }}
                                    >
                                      <ChevronRight />
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                // Before the first visit: the onboarding chief complaint. Feedback
                                // lives on the claims insights below, not on this line.
                                <p style={{
                                  margin: 0,
                                  fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em',
                                  color: 'var(--evergreen)', fontFamily: 'var(--font-body)',
                                }}>
                                  {patient.onboardingChiefComplaint}
                                </p>
                              )}

                              {/* Summary + Claims: the claims insights stay beneath the chief
                                  complaint, under a divider, exactly as they show before the
                                  first visit. After the first visit they drop away. */}
                              {isSummaryReady && (
                                <div style={{ borderTop: '1px solid var(--steel)', paddingTop: 16 }}>
                                  <PreVisitClaimsInsights state={effectiveClaimsState} />
                                </div>
                              )}
                            </>
                            )
                          ) : (
                            <CareContinuityPanel
                              goal={patient.careGoal}
                              source={patient.careGoalSource}
                              symptomStatus={goalSymptomStatus}
                              onOpen={() => openBlock(patient.careGoalBlockId)}
                              hasHadFirstVisit={isSteadyState}
                            />
                          )}
                        </div>
                      </chakra.div>

                      {/* Stats row — C_Card/Stats spec */}
                      <div style={{ display: 'flex', gap: 'var(--space-s)', marginTop: 12 }}>
                        <StatCard
                          eyebrow="Symptom Change"
                          value={patient.symptomChange.value}
                          detail={`Baseline: ${patient.symptomChange.baseline}`}
                          trend={patient.symptomChange.trend}
                          onClick={() => { /* TODO: navigate to symptom detail */ }}
                          width={194}
                        />
                        <StatCardDouble
                          left={{
                            eyebrow: 'Gut Checks',
                            // Before baseline-complete there's no gut-check count, last-survey
                            // date, or Nudge action to show yet — just the label + status pill.
                            ...(resolvedAppStatus === 'baseline-complete'
                              ? {
                                  value: patient.gutChecks.value,
                                  detail: `Last Survey: ${patient.gutChecks.lastSurvey}`,
                                  action: { label: 'Nudge', onClick: () => { /* TODO: send nudge */ } },
                                }
                              : {}),
                            statusIndicator: (
                              <GutCheckAppStatus status={resolvedAppStatus} />
                            ),
                          }}
                          right={{
                            eyebrow: 'Team Trust Score',
                            value: patient.teamTrustScore.value,
                            detail: `Member Average: ${patient.teamTrustScore.memberAverage}`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Right — Requested Follow-ups (C_Follow-ups spec) */}
                    <div style={{ width: 280, flexShrink: 0 }}>
                      <FollowUpsCard items={patient.requestedFollowUps} />
                    </div>
                  </div>

                  {/* Care Guide kanban */}
                  <div>
                    {/* Section header */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <h2 style={{
                        fontSize: 'var(--text-md-b-size)', fontWeight: 'var(--text-md-b-weight)', color: 'var(--evergreen)',
                        fontFamily: 'var(--font-body)', margin: 0,
                      }}>
                        Care Guide
                      </h2>
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        <Pressable
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap="6px"
                          borderWidth="1px"
                          borderColor="cloud"
                          borderRadius="8px 0 0 8px"
                          px={3}
                          py={2}
                          fontSize="sm"
                          color="evergreen"
                          fontFamily="body"
                          bg="canvas"
                        >
                          Filter
                        </Pressable>
                        <div style={{ width: 1, height: 36, background: 'var(--cloud)' }} />
                        <Pressable
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap={1}
                          borderWidth="1px"
                          borderColor="cloud"
                          borderLeft="none"
                          borderRadius="0 8px 8px 0"
                          px={3}
                          py={2}
                          fontSize="sm"
                          color="evergreen"
                          fontFamily="body"
                          bg="canvas"
                        >
                          Active
                          <ChevronDown />
                        </Pressable>
                      </div>
                    </div>

                    {/* Swim lanes — C_Care-Guide column container + LaneBlockCard stack */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      {CARE_PLAN_LANES.map(lane => (
                        <chakra.div
                          key={lane.id}
                          css={{ background: '{colors.feather}', borderRadius: '{radii.card}' }}
                          style={{
                            flex: 1, minWidth: 0,
                            padding: 'var(--space-s)',
                            display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)',
                          }}
                        >

                          {/* C_Care-Guide column header — see C_care-kanban-design-spec.md §15 */}
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--space-xs)',
                            height: 32, minWidth: 0,
                          }}>
                            <span className="md-body" style={{
                              flex: 1, minWidth: 0,
                              fontWeight: 600, color: 'var(--evergreen)',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {lane.label}
                            </span>
                            <span style={{
                              flexShrink: 0,
                              fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)', lineHeight: 'var(--text-xs-b-lh)', letterSpacing: '0.01em',
                              background: 'var(--evergreen)', color: '#fff',
                              borderRadius: 4, padding: '2px 8px',
                              fontFamily: 'var(--font-body)',
                            }}>
                              {lane.blocks.length}
                            </span>
                            <Pressable
                              type="button"
                              aria-label={`Add ${lane.label} block`}
                              flexShrink={0}
                              w="24px"
                              h="24px"
                              p={0}
                              color="evergreen"
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              borderRadius="full"
                            >
                              <PlusIcon />
                            </Pressable>
                          </div>

                          {/* Cards */}
                          {lane.blocks.length > 0 ? (
                            lane.blocks.map(block => (
                              <KanbanCard key={block.id} block={block} onOpen={() => openBlock(block.id)} />
                            ))
                          ) : (
                            <div style={{
                              border: '1.5px dashed var(--cloud)', borderRadius: 'var(--radius-card)',
                              padding: '24px 12px', textAlign: 'center',
                              background: 'var(--canvas)',
                            }}>
                              <span style={{ fontSize: 'var(--text-xs-size)', color: 'var(--steel)', fontFamily: 'var(--font-body)' }}>
                                No active blocks
                              </span>
                            </div>
                          )}
                        </chakra.div>
                      ))}
                    </div>
                  </div>

            </div>
          ) : activeNav === 'Labs & Documents' ? (
            <LabsTab
              patientId={patientId}
              journeyStage={journeyStage ?? 'profile'}
              setJourneyStage={setJourneyStage ?? (() => {})}
              activeLabOrder={activeLabOrder ?? null}
              setActiveLabOrder={setActiveLabOrder ?? (() => {})}
              onLabOrderComplete={onLabOrderComplete}
            />
          ) : activeNav === 'Pharmacy' ? (
            <PharmacyTab patientId={patientId} />
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', color: 'var(--blue-grey)', fontFamily: 'var(--font-body)', fontSize: 'var(--text-sm-size)' }}>
              {activeNav} — coming soon
            </div>
          )}
        </div>

        {/* C_Sidebar/Patient — right rail */}
        <PatientSidebar
          patient={patient}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        />
      </div>

      {/* Care block detail — slides in over the profile, including the rail. */}
      {/* Patient summary detail — opened from the steady-state summary card. */}
      <PatientSummaryDrawer
        isOpen={summaryOpen}
        detail={patient.summaryDetail}
        onClose={() => setSummaryOpen(false)}
      />

      <CareBlockDrawer
        blockId={openBlockId}
        memberName={patient.name}
        onClose={() => setOpenBlockId(null)}
      />
    </chakra.div>
  )
}
